import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { calendarMonths, yearEndEntry } from '@/mocks/scheduleData';

const courseColors: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  essential: {
    bg: 'bg-accent-500/12',
    text: 'text-accent-600',
    border: 'border-accent-500/30',
    dot: 'bg-accent-500',
  },
  advanced: {
    bg: 'bg-accent-400/10',
    text: 'text-accent-500',
    border: 'border-accent-400/25',
    dot: 'bg-accent-400',
  },
  master: {
    bg: 'bg-accent-600/10',
    text: 'text-accent-700',
    border: 'border-accent-600/25',
    dot: 'bg-accent-600',
  },
  break: {
    bg: 'bg-foreground-200/5',
    text: 'text-foreground-400',
    border: 'border-foreground-200/15',
    dot: 'bg-foreground-300',
  },
  double: {
    bg: 'bg-accent-500/10',
    text: 'text-accent-600',
    border: 'border-accent-500/25',
    dot: 'bg-gradient-to-r from-accent-600 to-accent-400',
  },
};

const courseLinkMap: Record<string, string> = {
  essential: '/courses/essential',
  advanced: '/courses/advanced',
  master: '/courses/master',
};

export default function AnnualCalendar() {
  const { t } = useTranslation();
  return (
    <section className="relative py-20 md:py-28 bg-background-50">
      <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="text-center mb-12">
          <p className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">
            CALENDAR
          </p>
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('schedule.calendar.altTitle')}
          </h2>
          {String(t('schedule.calendar.titleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">{__s}</p>))}
          <div className="gold-line mx-auto"></div>
          {String(t('schedule.calendar.altDescription')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-500 tracking-[0.04em] leading-relaxed max-w-[650px] mx-auto mt-5">{__s}</p>))}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center justify-center gap-5 mb-10">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-accent-500"></div>
            <span className="text-[13px] text-foreground-600 tracking-[0.04em]">{t('schedule.calendar.course.essential')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-accent-400"></div>
            <span className="text-[13px] text-foreground-600 tracking-[0.04em]">{t('schedule.calendar.course.advanced')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-accent-600"></div>
            <span className="text-[13px] text-foreground-600 tracking-[0.04em]">{t('schedule.calendar.course.master')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-foreground-300"></div>
            <span className="text-[13px] text-foreground-600 tracking-[0.04em]">{t('schedule.calendar.course.break')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gradient-to-r from-accent-600 to-accent-400"></div>
            <span className="text-[13px] text-foreground-600 tracking-[0.04em]">{t('schedule.calendar.course.double')}</span>
          </div>
        </div>

        {/* Desktop grid */}
        <div className="hidden md:grid grid-cols-4 gap-4">
          {calendarMonths.map((m) => {
            const c = courseColors[m.course];
            const link = courseLinkMap[m.course];
            const isBreak = m.course === 'break';

            const content = (
              <div
                className={`border ${c.border} ${c.bg} p-5 ${!isBreak ? 'card-glow cursor-pointer' : ''} group`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[13px] text-foreground-500 tracking-[0.06em]">{t(`schedule.calendar.month.${m.month}`)}</span>
                  <div className={`w-2.5 h-2.5 rounded-full ${c.dot}`}></div>
                </div>
                <p className={`text-[14px] font-medium tracking-[0.06em] mb-1 ${c.text}`}>
                  {t(`schedule.calendar.course.${m.course}`)}
                </p>
                <p className="text-[12px] text-foreground-400 tracking-[0.05em] mb-2">{m.subLabel}</p>
                <p className="text-[12px] text-foreground-500 tracking-[0.03em] leading-relaxed">
                  {t(`schedule.calendar.desc.${m.course}`)}
                </p>
              </div>
            );

            if (isBreak) return <div key={m.month}>{content}</div>;
            return (
              <Link key={m.month} to={link}>
                {content}
              </Link>
            );
          })}

          {/* Year-end break */}
          <div className="border border-foreground-200/15 bg-foreground-200/5 p-5 col-span-full">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[13px] text-foreground-500 tracking-[0.06em]">{t('schedule.calendar.yearEnd.label')}</span>
              <div className="w-2.5 h-2.5 rounded-full bg-foreground-300"></div>
            </div>
            {String(t('schedule.calendar.yearEnd.courseLabel')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[14px] text-foreground-400 tracking-[0.06em] mb-1">{__s}</p>))}
            {String(t('schedule.calendar.yearEnd.description')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[12px] text-foreground-500 tracking-[0.03em] leading-relaxed">{__s}</p>))}
          </div>
        </div>

        {/* Mobile list */}
        <div className="md:hidden space-y-3">
          {calendarMonths.map((m) => {
            const c = courseColors[m.course];
            const link = courseLinkMap[m.course];
            const isBreak = m.course === 'break';

            const content = (
              <div className={`border ${c.border} ${c.bg} p-4 flex items-center gap-4 ${!isBreak ? 'card-glow cursor-pointer' : ''}`}>
                <div className="flex-shrink-0 w-12 text-center">
                  <span className="text-[16px] text-foreground-800 font-medium tracking-[0.06em]">{t(`schedule.calendar.month.${m.month}`)}</span>
                </div>
                <div className="flex-1">
                  <p className={`text-[15px] font-medium tracking-[0.06em] ${c.text}`}>{t(`schedule.calendar.course.${m.course}`)}</p>
                  <p className="text-[12px] text-foreground-400 tracking-[0.05em]">{m.subLabel}</p>
                </div>
                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${c.dot}`}></div>
              </div>
            );

            if (isBreak) return <div key={m.month}>{content}</div>;
            return (
              <Link key={m.month} to={link}>
                {content}
              </Link>
            );
          })}

          <div className="border border-foreground-200/15 bg-foreground-200/5 p-4 flex items-center gap-4">
            <div className="flex-shrink-0 w-12 text-center">
              <span className="text-[16px] text-foreground-800 font-medium tracking-[0.06em]">{t('schedule.calendar.yearEnd.label')}</span>
            </div>
            <div className="flex-1">
              {String(t('schedule.calendar.yearEnd.courseLabel')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-400 tracking-[0.06em]">{__s}</p>))}
              <p className="text-[12px] text-foreground-400 tracking-[0.05em]">{yearEndEntry.subLabel}</p>
            </div>
            <div className="w-2.5 h-2.5 rounded-full bg-foreground-300 flex-shrink-0"></div>
          </div>
        </div>

        {String(t('schedule.calendar.footerNote')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-center text-[13px] text-foreground-400 tracking-[0.04em] mt-8">{__s}</p>))}
      </div>
    </section>
  );
}
